import React, { useContext, useEffect } from 'react';
import Helmet from 'react-helmet';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { Link } from '@latitude/link';
import { AccordionSidebar } from '@latitude/accordion-rebrand';
import MobileAppInstallPrompts from '@latitude/mobile-app-install-prompt/MobileAppInstallPrompt';
import Button from '@latitude/button-rebrand';
import Layout from '@/components/layout';
import '../gem-visa-card.scss';
import { getUrlFromEnv } from '../../../utils/getUrlFromEnvUtil';
import faqsCredit from '../_faqs';
import { PageContext } from '../../../context/PageContext';
import { getAccordionSidebarData } from '@/utils/helpers';
import { sourcePromoCodeHandler } from '@/utils/sourcePromoCodeHandler';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrand';
import LabRatesAndFeesColumn from '@/components/LabRatesAndFeesColumnRebrand';
import SvgInline from '@latitude/svg-inline';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import withStickyState from '../../../hoc/withStickyState';
import StickyNavigation from '@/components/StickyNavigationRebrand/StickyNavigation';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import CalloutHelp from '@/components/CalloutHelpRebrand/CalloutHelpRebrand';
import Section from '@latitude/section-rebrand';
import Typography from '@latitude/typography';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import PageData from '@/data/pages/gem-visa-card.json';
import GemMicrositeHeader from './_gem-microsite-header-rebrand';

const GemVisaExclusiveOfferPage = () => {
  const applyUrl = getUrlFromEnv('funnel-eapps-start-application');
  const callOutUrl =
    'https://cards.gemvisa.co.nz/?promo_code=7QCV6FKC';

  const PAGE_NAVIGATION = [
    {
      anchor: 'benefits',
      label: 'Why Gem Visa?'
    },
    {
      anchor: 'rates',
      label: 'Rates, Fees & Conditions Of Use'
    },
    {
      anchor: 'payment-options',
      label: 'Payment Options'
    },
    {
      anchor: 'how-to-apply',
      label: 'How to apply'
    },
    {
      anchor: 'faq',
      label: 'FAQs'
    }
  ];

  const MICROSITE_HEADER_DATA = {
    navItems: [
      {
        id: 'balance-transfer',
        label: 'Balance Transfer',
        trackId: 'nav-balance-transfer',
        href: '/credit-cards/gem-visa-card/balance-transfer/'
      },
      {
        id: 'interest-free-offers',
        label: 'Interest Free Offers',
        trackId: 'nav-interest-free-offers',
        href: '/credit-cards/where-to-shop/'
      },
      {
        id: 'help',
        label: 'Help',
        trackId: 'help',
        href:
          'https://gemfinance.zendesk.com/hc/en-nz/categories/360001320618-Credit-Cards'
      }
    ],
    applyNowUrl:
      'https://cards.gemvisa.co.nz/?promo_code=8RGTUZLY&source_code=IDAAA',
    trademark: true
  };

  const FEATURE_GRID_DATA = {
    background: REBRAND_COLOR.STONE,
    title: 'What makes Gem Visa different?',
    features: [
      {
        icon: <SvgInline name="icon-shopping-cart-simple-rebrand" />,
        headingText: (
          <>
            0% interest for 6 months on general purchases of $250 or more
            <sup>*</sup>
          </>
        ),
        paragraphText: (
          <>
            Select your purchases in the Latitude App or Latitude Service Centre
            within 30 days of the purchase date.
          </>
        )
      },
      {
        icon: <SvgInline name="icon-calender-dots-rebrand" />,
        headingText: '0% interest offers from 6 to 60 months',
        paragraphText: 'Take the stress out of big purchases with 0% interest payment plans at 100s of retailers online and in-store.'
      },
      {
        icon: <SvgInline name="icon-device-mobile-rebrand" />,
        headingText: 'Top-rated app to complement your card',
        paragraphText: "With a 4.8/5 App store rating, the Latitude App helps you stay on top of your payments and never miss a due date.",
      },
      {
        icon: <SvgInline name="icon-users-rebrand" />,
        headingText: '$0 additional card holder fee',
        paragraphText:
          'Share all the features and benefits without needing to open a second account.',
      }
    ]
  };

  const HOW_TO_APPLY_DATA = {
    id: 'how-to-apply',
    howToApply: {
      sectionTitle: 'How To apply',
      items: [
        {
          title: 'Apply online',
          description: 'Check your eligibility and apply online.'
        },
        {
          title: 'Wait a minute',
          description:
            '60 seconds is all it takes for us to respond to your application (responsible lending criteria applies). If we require further details, the response may take up to 5 business days.'
        },
        {
          title: 'Ready, set, shop',
          description:
            'Instant cardless payment feature where you can apply and once approved start shopping within minutes.'
        }
      ]
    },
    eligibility: {
      sectionTitle: 'Eligibility',
      items: [
        {
          title: 'You must:',
          list: [
            'Be 18 years or over',
            'Be a permanent resident of New Zealand'
          ]
        },
        {
          title: 'Able to supply:',
          list: [
            "Passport or Driver's License",
            'Income and expense details',
            "Employer's name and contact details",
            'Your email address'
          ]
        }
      ]
    }
  };

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};

  useEffect(() => {
    const container = document.querySelector('.labFeesAndRatesColumn');
    const links = container?.querySelectorAll('a');
    links?.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
  }, []);

  return (
    <div
      css={`
        .top-menu-items, .search-button, .logon-button-wrapper{
          visibility: hidden !important;
        }
      `}
    >
      <Layout hasStickyNav minimalHeader>
        <MobileAppInstallPrompts />
        <main
          css={`
            [class^='HowToApply__StyledConfetti'] {
              display: none;
            }
          `}
        >
          <Helmet>
            <link
              rel="canonical"
              href="https://www.gemfinance.co.nz/credit-cards/gem-visa-card/"
            />
            <title>
              Gem Visa Credit Card | Interest Free Credit Cards NZ | Gem by
              Latitude
            </title>
            <meta
              name="description"
              content="Enjoy 6 months int. free on all Gem Visa purchases of $250 and over. T&amp;Cs and $65 annual (charged $32.50 half-yearly) fees apply. Int. rate 29.99% p.a. applies after int. free term ends."
            />
          </Helmet>

          {/* <GemMicrositeHeader data={MICROSITE_HEADER_DATA} /> */}

          <div style={{ paddingTop: '60px' }}>
            <HeroV2 {...state?.heroBannerData?.[0] || PageData?.heroBannerData?.[0]} />
          </div>
          <div className="d-none d-lg-block" data-sticky-navigation-offset>

            {/* <StickyNavigation
            items={PAGE_NAVIGATION}
            enableAnchorUpdate={true}
            trackProductId={['CCNZGEM']}
            stickyOffset={146}
            phoneNumber="0800 500 505"
            ctaUrl="https://cards.gemvisa.co.nz/?promo_code=8RGTUZLY&source_code=IDAAA"
            ctaText="Apply now"
          /> */}
          </div>


          <FeatureGridV1
            id="benefits"
            trackingId="gem-nz"
            {...FEATURE_GRID_DATA}
          />

          {state?.ratesAndFeesData?.[0] && (<LabRatesAndFeesColumn isRate={true} {...state?.ratesAndFeesData?.[0]} />)}

          <HowToApply data={HOW_TO_APPLY_DATA} />

          <Section
            id="faq"
            data-contentful={state?.faqData?.[0]?.contentfulID}
            css={`
              background-color: ${REBRAND_COLOR.WHITE};
              padding-top: ${PADDING.P32};
              padding-bottom: ${PADDING.P32};
              display: flex;
              flex-direction: column;
              gap: 32px;
              a {
                color: ${REBRAND_COLOR.MID_BLUE};
                border-bottom: 1px solid ${REBRAND_COLOR.MID_BLUE};
              }
              .accordion__item .accordion__item p:last-of-type {
                padding-top: 16px !important;
              }
            `}
          >
            <Typography.H4>
              {state?.faqData?.[0]?.title || 'Frequently asked questions'}
            </Typography.H4>
            <AccordionSidebar
              data={
                getAccordionSidebarData(state?.faqData?.[0]?.data) ||
                faqsCredit.gemVisaCard
              }
            />
          </Section>

          <CalloutHelp
            heading={<>We're here to help</>}
            desc={
              <>
                <Link
                  href={'https://gemfinance.zendesk.com/hc/en-nz'}
                  trackEventData={{
                    label: 'chat-to-us-live',
                    location: 'we-are-here-to-help'
                  }}
                >
                  Chat to us live
                </Link>
                <br />
                online Mon to Fri 8:00am - 6:30pm.
                <br />
                <br />
                Call our Gem Visa team on{' '}
                <Link
                  href={'tel:0800 500 505'}
                  trackEventData={{
                    label: `0800 500 505`,
                    location: 'we-are-here-to-help'
                  }}
                >
                  0800 500 505
                </Link>
                <br />
                Mon to Fri 09:00am - 6:30pm (NZST)
                <br />
                Sat: 11:00am to 08:00pm
                <br />
                Sun: Closed
              </>
            }
            buttonComponent={
              <Button
                variant="primary"
                ctaUrl={sourcePromoCodeHandler(callOutUrl)}
                ctaText="Apply now"
                trackId="help-banner-apply"
                trackEventData={{
                  category: 'cta',
                  action: 'application-link',
                  location: "We're here to help"
                }}
                trackProductId={['CCNZGEM']}
              />
            }
          />
          <ImportantInformation
            id="important-information"
            css={`
              overflow-x: hidden;
              .row {
                @media (min-width: 1600px) {
                  padding-left: ${PADDING.P32};
                  padding-right: ${PADDING.P32};
                }
              }
            `}
            data={require('../../../data/json/disclaimer/gem-visa.json')}
            {...{
              ...state?.importantInfoData?.[0],
              sectionOneColummClasses: 'col-12 col-lg-6',
              sectionTwoColummClasses: 'col-12 col-lg-6'
            }}
          />
        </main>
      </Layout>
    </div>
  );
};


const GemVisaExclusiveOfferPageWithSticky = withStickyState(GemVisaExclusiveOfferPage);

export default GemVisaExclusiveOfferPageWithSticky;